/**
 * Conversion between **frame-relative** coordinates and on-screen pixels.
 *
 * Anything drawn or pinned onto the video frame — review annotations today,
 * potentially other frame-anchored chrome later — must survive a window resize,
 * pseudo-fullscreen, and being reopened on a different display. Pixels do not;
 * fractions of the frame do. So the stored form is always a pair in `[0, 1]`
 * with the origin at the frame's **top-left**, and pixels are derived at paint
 * time from whatever rect the frame currently occupies.
 *
 * The rect to convert against is the **video frame**, which is not always the
 * element that hosts it. In the clip preview the player element is itself
 * aspect-ratio-shaped, so its bounding rect *is* the frame (the Creatomate
 * iframe inside is deliberately overscanned — `top/left: -12.5%`, `125%` wide —
 * and cropped by the container's `overflow: hidden`, so measuring the iframe
 * would be wrong by that inset). Where the host is not aspect-shaped, such as
 * the Creator Studio stage, `useRendererRect` computes the inset frame box and
 * that is what should be passed here.
 *
 * Pair with `useViewportRect` (`@src/hooks/useViewportRect`) to obtain a live
 * rect for a given element.
 */

/**
 * A rect in **viewport** space, as returned by `getBoundingClientRect`.
 * `left` / `top` matter only for the capture direction; rendering inside an
 * overlay already positioned on the frame needs `width` / `height` alone.
 */
export interface FrameRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** A point on the frame, `[x, y]`, each in `[0, 1]` from the top-left. */
export type NormalizedPoint = [number, number];

/** A point in pixels, local to the frame's own box. */
export interface LocalPoint {
  x: number;
  y: number;
}

/** Clamp to the `[0, 1]` range a normalized coordinate is defined over. */
export function clamp01(value: number): number {
  if (Number.isNaN(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

/**
 * Capture direction: a pointer event's viewport coordinates → frame-relative.
 *
 * **Clamped on purpose.** A drag that leaves the frame — which is normal, since
 * people overshoot — would otherwise record points outside `[0, 1]` that paint
 * outside the frame on read-back. Clamping pins the stroke to the frame edge
 * instead, which is what the person drawing meant.
 *
 * A zero-area rect yields `[0, 0]` rather than dividing by zero; callers get a
 * degenerate but harmless point instead of `NaN` propagating into stored data.
 */
export function toNormalizedPoint(
  clientX: number,
  clientY: number,
  rect: FrameRect,
): NormalizedPoint {
  if (rect.width <= 0 || rect.height <= 0) return [0, 0];
  return [
    clamp01((clientX - rect.left) / rect.width),
    clamp01((clientY - rect.top) / rect.height),
  ];
}

/**
 * Render direction: frame-relative → pixels **local to the frame box**.
 *
 * Local rather than viewport-absolute because the overlay that paints these is
 * already positioned on the frame, so its own coordinate space starts at the
 * frame's top-left. Add `rect.left` / `rect.top` if you genuinely need viewport
 * coordinates.
 *
 * Not clamped: stored points are clamped on capture, and silently moving a
 * point that somehow sits outside the range would hide the fact that the data
 * is wrong.
 */
export function toLocalPoint(
  point: NormalizedPoint,
  rect: FrameRect,
): LocalPoint {
  return { x: point[0] * rect.width, y: point[1] * rect.height };
}

/**
 * Render direction for a **length** — stroke width, hit radius — expressed as a
 * fraction of the frame's height.
 *
 * Height is the reference so that thickness scales uniformly as the player is
 * resized. Width would do equally well for a fixed aspect ratio, and it does
 * not matter which we picked as long as it never changes: a drawing is scoped
 * to the aspect ratio it was made on, so a stored width is only ever rendered
 * against frames of one shape.
 */
export function toLocalLength(fraction: number, rect: FrameRect): number {
  return fraction * rect.height;
}
